import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

import {
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetAllGroupsQuery,
  useUpdateGroupMutation,
} from "../../redux/admin/groupApi";
import AddDeviceToGroupModal from "./AddDeviceToGroupModal";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getRowsPerPageLabel,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";

const defaultForm = {
  id: "",
  name: "",
  contact: "",
  groupType: "fleet",
};

const getGroupContact = (group) =>
  group?.description?.trim() || group?.owner?.mobileNo || group?.owner?.email || "-";

const pickFiniteNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const getVehicleListFromGroup = (group) => {
  const candidates = [
    group?.vehicles,
    group?.devices,
    group?.assignedDevices,
    group?.deviceIds,
    group?.vehicleIds,
    group?.members,
  ];
  return candidates.find(Array.isArray) || [];
};

const getGroupVehicleCount = (group) => {
  const explicitCount = pickFiniteNumber(
    group?.vehicleCount,
    group?.vehiclesCount,
    group?.totalVehicles,
    group?.deviceCount,
    group?.devicesCount
  );
  if (explicitCount !== null) return explicitCount;
  return getVehicleListFromGroup(group).length;
};

const getGroupStatusLabel = (group) => {
  const raw = String(group?.status || group?.groupStatus || "").trim().toLowerCase();
  if (raw) {
    if (["inactive", "disabled", "archived"].includes(raw)) return "Inactive";
    return "Active";
  }
  if (group?.isActive === false) return "Inactive";
  return "Active";
};

const statCardConfig = [
  {
    key: "groups",
    label: "Total Groups",
    icon: <Groups2OutlinedIcon />,
    iconBg: "rgba(37,99,235,0.10)",
    iconColor: "#2563eb",
  },
  {
    key: "members",
    label: "Total Members",
    icon: <Groups2OutlinedIcon />,
    iconBg: "rgba(34,197,94,0.10)",
    iconColor: "#16a34a",
  },
  {
    key: "managers",
    label: "Managers",
    icon: <ShieldOutlinedIcon />,
    iconBg: "rgba(245,158,11,0.10)",
    iconColor: "#9333ea",
  },
  {
    key: "viewers",
    label: "Viewers",
    icon: <RemoveRedEyeOutlinedIcon />,
    iconBg: "rgba(239,68,68,0.10)",
    iconColor: "#f59e0b",
  },
];

const GroupsList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [form, setForm] = useState(defaultForm);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState("");
  const [assignGroupId, setAssignGroupId] = useState("");
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [moreGroupId, setMoreGroupId] = useState("");

  const queryParams = useMemo(
    () => ({
      page: rowsPerPage === -1 ? 1 : page,
      limit: rowsPerPage === -1 ? 1000 : rowsPerPage,
      search: search.trim() || undefined,
    }),
    [page, rowsPerPage, search]
  );

  const { data, isLoading, isFetching, refetch } = useGetAllGroupsQuery(queryParams);
  const { data: summaryData } = useGetAllGroupsQuery({ page: 1, limit: 1000 });
  const [createGroup, { isLoading: creating }] = useCreateGroupMutation();
  const [updateGroup, { isLoading: updating }] = useUpdateGroupMutation();
  const [deleteGroup, { isLoading: deleting }] = useDeleteGroupMutation();

  const groups = Array.isArray(data?.data) ? data.data : [];
  const summaryGroups = Array.isArray(summaryData?.data) ? summaryData.data : groups;
  const totalCount = Number(data?.pagination?.totalCount || summaryGroups.length || groups.length || 0);
  const totalPages =
    rowsPerPage === -1
      ? 1
      : Math.max(1, Number(data?.pagination?.totalPages || Math.ceil(totalCount / rowsPerPage) || 1));
  const displayRange = useMemo(() => getDisplayRange(totalCount, page, rowsPerPage), [page, rowsPerPage, totalCount]);
  const busy = creating || updating;

  const summaryStats = useMemo(
    () => ({
      groups: totalCount,
      members: summaryGroups.reduce((sum, group) => sum + getGroupVehicleCount(group), 0),
      managers: summaryGroups.reduce((sum, group) => sum + ((group?.managers || []).filter((manager) => manager?.role !== "owner").length || 0), 0),
      viewers: summaryGroups.reduce((sum, group) => sum + ((group?.viewers || []).length || 0), 0),
    }),
    [summaryGroups, totalCount]
  );

  const activeGroupCount = useMemo(
    () => summaryGroups.filter((group) => getGroupStatusLabel(group) === "Active").length,
    [summaryGroups]
  );
  const inactiveGroupCount = Math.max(0, totalCount - activeGroupCount);
  const trendPoints = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 7 }).map((_, index) => {
      const date = new Date(now);
      date.setDate(now.getDate() - (6 - index));
      const key = date.toISOString().slice(0, 10);
      const count = summaryGroups.filter((group) => {
        const createdAt = String(group?.createdAt || group?.created_at || "");
        return createdAt.slice(0, 10) === key;
      }).length;
      return {
        label: date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        value: count,
      };
    });
  }, [summaryGroups]);
  const trendMax = Math.max(1, ...trendPoints.map((point) => point.value));

  const openCreate = () => {
    setForm(defaultForm);
    setFormOpen(true);
  };

  const openEdit = (group) => {
    setForm({
      id: group?._id || "",
      name: group?.name || "",
      contact: group?.description || "",
      groupType: group?.groupType || "fleet",
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setForm(defaultForm);
    setFormOpen(false);
  };

  const closeMoreMenu = () => {
    setMoreAnchorEl(null);
    setMoreGroupId("");
  };

  const handleOpenMore = (event, groupId) => {
    setMoreAnchorEl(event.currentTarget);
    setMoreGroupId(groupId);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setStatus({ type: "error", message: "Group name is required." });
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        description: form.contact.trim(),
        groupType: form.groupType || "fleet",
      };

      if (form.id) {
        await updateGroup({ id: form.id, ...payload }).unwrap();
        setStatus({ type: "success", message: "Group updated successfully." });
      } else {
        await createGroup(payload).unwrap();
        setStatus({ type: "success", message: "Group created successfully." });
      }

      closeForm();
      refetch();
    } catch (error) {
      setStatus({ type: "error", message: error?.data?.msg || "Failed to save group." });
    }
  };

  const handleDelete = async () => {
    if (!deleteGroupId) return;
    try {
      await deleteGroup({ id: deleteGroupId }).unwrap();
      setDeleteGroupId("");
      setStatus({ type: "success", message: "Group deleted successfully." });
      refetch();
    } catch (error) {
      setStatus({ type: "error", message: error?.data?.msg || "Failed to delete group." });
    }
  };

  const tableHeadCellSx = {
    borderBottom: "1px solid rgba(226,232,240,0.9)",
    color: "#64748b",
    fontSize: "0.85rem",
    fontWeight: 700,
    py: 2.25,
  };

  const actionButtonSx = {
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: "1px solid rgba(226,232,240,0.95)",
    bgcolor: "#fff",
    color: "#64748b",
    boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
    "&:hover": {
      borderColor: "rgba(191,219,254,0.95)",
      bgcolor: "#f8fbff",
      color: "#2563eb",
    },
  };

  const renderRowActions = (group) => (
    <Stack direction="row" spacing={1}>
      <Tooltip title="View Group">
        <IconButton
          sx={actionButtonSx}
          onClick={() =>
            navigate(`/groups/${group._id}`, {
              state: { group },
            })
          }
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit Group">
        <IconButton sx={actionButtonSx} onClick={() => openEdit(group)}>
          <EditOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="More">
        <IconButton sx={actionButtonSx} onClick={(event) => handleOpenMore(event, group._id)}>
          <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box
      sx={{
        minHeight: "100%",
        background:
          "radial-gradient(circle at top right, rgba(37,99,235,0.06), transparent 24%), linear-gradient(180deg, #f7f9fd 0%, #f3f6fb 100%)",
        px: { xs: 0, sm: 0.25, md: 0.5 },
        py: { xs: 0.5, sm: 0.75, md: 1 },
      }}
    >
      <Box sx={{ maxWidth: 1480, mx: "auto" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography sx={{ color: "#0f172a", fontSize: { xs: "1.65rem", md: "2rem" }, fontWeight: 800, letterSpacing: "-0.03em" }}>
                Groups
              </Typography>
              <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: "0.96rem" }}>
                Organize and manage your vehicle groups
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ justifyContent: { xs: "stretch", md: "flex-end" } }}>
              <TextField
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Search anything..."
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 300 },
                  "& .MuiOutlinedInput-root": {
                    height: 48,
                    borderRadius: "14px",
                    bgcolor: "#fff",
                    boxShadow: "0 10px 28px rgba(15,23,42,0.04)",
                    "& fieldset": {
                      borderColor: "rgba(226,232,240,0.95)",
                    },
                  },
                }}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(12, minmax(0, 1fr))" },
              gap: 1.4,
              mb: 2.25,
            }}
          >
            {statCardConfig.map((card) => (
              <Paper
                key={card.key}
                elevation={0}
                sx={{
                  gridColumn: { xs: "span 1", lg: "span 2" },
                  p: { xs: 2, lg: 2.1 },
                  borderRadius: "20px",
                  border: "1px solid rgba(226,232,240,0.92)",
                  boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box sx={{ width: { xs: 52, lg: 46 }, height: { xs: 52, lg: 46 }, borderRadius: "999px", bgcolor: card.iconBg, color: card.iconColor, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  {card.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: { xs: "2rem", lg: "1.55rem" }, lineHeight: 1, fontWeight: 800, color: "#0f172a" }}>
                    {summaryStats[card.key].toLocaleString("en-IN")}
                  </Typography>
                  <Typography sx={{ mt: 0.55, fontSize: { xs: "0.95rem", lg: "0.86rem" }, color: "#64748b" }}>{card.label}</Typography>
                </Box>
              </Paper>
            ))}

            <Paper
              elevation={0}
              sx={{
                gridColumn: { xs: "span 1", lg: "span 4" },
                p: { xs: 2, lg: 2.1 },
                borderRadius: "20px",
                border: "1px solid rgba(226,232,240,0.92)",
                boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
              }}
            >
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Group Status Overview
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 170,
                    height: 170,
                    borderRadius: "50%",
                    background: `conic-gradient(#22c55e 0 ${(activeGroupCount / Math.max(totalCount || 1, 1)) * 360}deg, #f59e0b 0 360deg)`,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ width: 112, height: 112, borderRadius: "50%", bgcolor: "#fff", display: "grid", placeItems: "center", textAlign: "center" }}>
                    <Typography sx={{ fontSize: "1.9rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{totalCount}</Typography>
                    <Typography sx={{ mt: 0.35, color: "#64748b", fontSize: "0.88rem" }}>Total</Typography>
                  </Box>
                </Box>
                <Stack spacing={1.25} sx={{ flex: 1, width: "100%" }}>
                  {[
                    { label: "Active", value: activeGroupCount, color: "#22c55e" },
                    { label: "Inactive", value: inactiveGroupCount, color: "#f59e0b" },
                  ].map((row) => (
                    <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: "999px", bgcolor: row.color }} />
                        <Typography sx={{ color: "#334155", fontSize: "0.95rem" }}>{row.label}</Typography>
                      </Stack>
                      <Typography sx={{ color: "#0f172a", fontWeight: 700 }}>
                        {row.value} ({totalCount ? Math.round((row.value / totalCount) * 100) : 0}%)
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                gridColumn: { xs: "span 1", xl: "span 4" },
                p: 2.5,
                borderRadius: "20px",
                border: "1px solid rgba(226,232,240,0.92)",
                boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" }}>
                  Groups Over Time
                </Typography>
                <Chip label="Last 7 Days" variant="outlined" sx={{ borderRadius: 2.5, fontWeight: 700 }} />
              </Stack>
              <Box sx={{ height: 200, display: "flex", alignItems: "flex-end", gap: 1.2, pt: 1 }}>
                {trendPoints.map((point) => (
                  <Stack key={point.label} spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: `${Math.max((point.value / trendMax) * 140, point.value ? 14 : 6)}px`,
                        borderRadius: "12px 12px 0 0",
                        background: "linear-gradient(180deg, rgba(37,99,235,0.95) 0%, rgba(96,165,250,0.26) 100%)",
                        boxShadow: "0 12px 24px rgba(37,99,235,0.18)",
                      }}
                    />
                    <Typography sx={{ color: "#64748b", fontSize: "0.76rem", textAlign: "center" }}>{point.label}</Typography>
                  </Stack>
                ))}
              </Box>
            </Paper>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(226,232,240,0.92)",
              boxShadow: "0 18px 42px rgba(15,23,42,0.05)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: "1px solid rgba(226,232,240,0.88)" }}>
              {status.message ? (
                <Alert severity={status.type || "info"} sx={{ mb: 2, borderRadius: 2.5 }}>
                  {status.message}
                </Alert>
              ) : null}

              <Stack direction={{ xs: "column", xl: "row" }} spacing={1.5} justifyContent="space-between">
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ flex: 1 }}>
                  <TextField
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search group name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#64748b" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: { xs: "100%", md: 360 },
                      "& .MuiOutlinedInput-root": {
                        height: 52,
                        borderRadius: "16px",
                        bgcolor: "#fff",
                        boxShadow: "0 10px 28px rgba(15,23,42,0.03)",
                        "& fieldset": {
                          borderColor: "rgba(226,232,240,0.95)",
                        },
                      },
                    }}
                  />

                  <Button
                    startIcon={<FilterListRoundedIcon />}
                    onClick={() => {}}
                    sx={{
                      height: 52,
                      minWidth: 110,
                      borderRadius: "16px",
                      border: "1px solid rgba(226,232,240,0.95)",
                      bgcolor: "#fff",
                      color: "#334155",
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "0 10px 28px rgba(15,23,42,0.03)",
                    }}
                  >
                    Filters
                  </Button>

                  <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 110 } }}>
                    <Select
                      value={rowsPerPage}
                      onChange={(event) => {
                        setRowsPerPage(Number(event.target.value));
                        setPage(1);
                      }}
                      MenuProps={ROWS_PER_PAGE_MENU_PROPS}
                      sx={{
                        height: 52,
                        borderRadius: "16px",
                        bgcolor: "#fff",
                        boxShadow: "0 10px 28px rgba(15,23,42,0.03)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(226,232,240,0.95)",
                        },
                      }}
                    >
                      {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>
                          {getRowsPerPageLabel(size)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={refetch}
                    sx={{
                      height: 52,
                      minWidth: 116,
                      borderRadius: "16px",
                      border: "1px solid rgba(226,232,240,0.95)",
                      bgcolor: "#fff",
                      color: "#334155",
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "0 10px 28px rgba(15,23,42,0.03)",
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreate}
                    sx={{
                      height: 52,
                      minWidth: 140,
                      borderRadius: "16px",
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "#1d63c8",
                      boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
                      "&:hover": {
                        bgcolor: "#114fae",
                      },
                    }}
                  >
                    Add Group
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {!isMobile ? (
              <TableContainer sx={{ px: 2, pt: 2, minHeight: 460 }}>
                <Table>
                  <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Group Name</TableCell>
                        <TableCell sx={tableHeadCellSx}>Contact</TableCell>
                        <TableCell sx={tableHeadCellSx}>Members</TableCell>
                        <TableCell sx={tableHeadCellSx}>Managers</TableCell>
                        <TableCell sx={tableHeadCellSx}>Viewers</TableCell>
                        <TableCell sx={tableHeadCellSx}>Status</TableCell>
                        <TableCell sx={tableHeadCellSx}>Created At</TableCell>
                        <TableCell sx={{ ...tableHeadCellSx, textAlign: "right", pr: 3 }}>Actions</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 10, color: "#64748b" }}>
                          Loading groups...
                        </TableCell>
                      </TableRow>
                    ) : groups.length ? (
                      groups.map((group) => {
                        const groupStatus = getGroupStatusLabel(group);
                        return (
                          <TableRow
                            key={group._id}
                            hover
                            sx={{
                              "& td": {
                                py: 2.5,
                                borderBottom: "1px solid rgba(241,245,249,1)",
                              },
                              "&:hover": {
                                bgcolor: "#fbfdff",
                              },
                            }}
                          >
                            <TableCell>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{ width: 42, height: 42, borderRadius: "14px", bgcolor: "#edf4ff", color: "#2563eb", display: "grid", placeItems: "center", flexShrink: 0 }}>
                                  <Groups2OutlinedIcon fontSize="small" />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                                  {group.name || "-"}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ color: "#334155", fontSize: "0.95rem" }}>{getGroupContact(group)}</TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                                {getGroupVehicleCount(group).toLocaleString("en-IN")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                                {((group?.managers || []).filter((manager) => manager?.role !== "owner").length || 0).toLocaleString("en-IN")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                                {((group?.viewers || []).length || 0).toLocaleString("en-IN")}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={groupStatus}
                                size="small"
                                sx={{
                                  height: 30,
                                  borderRadius: "999px",
                                  bgcolor: groupStatus === "Active" ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.14)",
                                  color: groupStatus === "Active" ? "#16a34a" : "#64748b",
                                  fontWeight: 700,
                                  px: 0.75,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "#334155", fontSize: "0.95rem" }}>
                              {group?.createdAt ? new Date(group.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right", pr: 3 }}>
                              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{renderRowActions(group)}</Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 10, color: "#64748b" }}>
                          No groups found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 1.5, display: "grid", gap: 1.5 }}>
                {isLoading ? (
                  <Paper sx={{ py: 8, textAlign: "center", color: "#64748b", borderRadius: 3 }}>Loading groups...</Paper>
                ) : groups.length ? (
                  groups.map((group) => {
                    const groupStatus = getGroupStatusLabel(group);
                    return (
                      <Paper
                        key={group._id}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "18px",
                          border: "1px solid rgba(226,232,240,0.95)",
                          boxShadow: "0 12px 26px rgba(15,23,42,0.04)",
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                          <Box sx={{ width: 42, height: 42, borderRadius: "14px", bgcolor: "#edf4ff", color: "#2563eb", display: "grid", placeItems: "center", flexShrink: 0 }}>
                            <Groups2OutlinedIcon fontSize="small" />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>{group.name || "-"}</Typography>
                            <Typography sx={{ color: "#64748b", fontSize: "0.85rem", mt: 0.25 }}>{getGroupContact(group)}</Typography>
                          </Box>
                          <Chip
                            label={groupStatus}
                            size="small"
                            sx={{
                              height: 28,
                              borderRadius: "999px",
                              bgcolor: groupStatus === "Active" ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.14)",
                              color: groupStatus === "Active" ? "#16a34a" : "#64748b",
                              fontWeight: 700,
                            }}
                          />
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                          <Box>
                            <Typography sx={{ color: "#64748b", fontSize: "0.78rem" }}>Members</Typography>
                            <Typography sx={{ color: "#0f172a", fontWeight: 700, mt: 0.2 }}>
                              {getGroupVehicleCount(group).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ color: "#64748b", fontSize: "0.78rem" }}>Managers</Typography>
                            <Typography sx={{ color: "#0f172a", fontWeight: 700, mt: 0.2 }}>
                              {((group?.managers || []).filter((manager) => manager?.role !== "owner").length || 0).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ color: "#64748b", fontSize: "0.78rem" }}>Viewers</Typography>
                            <Typography sx={{ color: "#0f172a", fontWeight: 700, mt: 0.2 }}>
                              {((group?.viewers || []).length || 0).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                          {renderRowActions(group)}
                        </Stack>
                        <Typography sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                          Created {group?.createdAt ? new Date(group.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                        </Typography>
                      </Paper>
                    );
                  })
                ) : (
                  <Paper sx={{ py: 8, textAlign: "center", color: "#64748b", borderRadius: 3 }}>No groups found.</Paper>
                )}
              </Box>
            )}

            <Box
              sx={{
                px: { xs: 1.5, md: 2.5 },
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                borderTop: "1px solid rgba(226,232,240,0.88)",
              }}
            >
              <Typography sx={{ color: "#475569", fontSize: "0.92rem" }}>
                Showing {displayRange.from} to {displayRange.to} of {totalCount} groups
              </Typography>
              <Pagination
                page={page}
                count={totalPages}
                onChange={(_, value) => setPage(value)}
                shape="rounded"
                sx={{
                  "& .MuiPagination-ul": {
                    gap: 0.6,
                  },
                  "& .MuiPaginationItem-root": {
                    borderRadius: "12px",
                    minWidth: 38,
                    height: 38,
                    border: "1px solid rgba(226,232,240,0.95)",
                    color: "#334155",
                    fontWeight: 700,
                    bgcolor: "#fff",
                  },
                  "& .Mui-selected": {
                    bgcolor: "#1d63c8 !important",
                    color: "#fff",
                    boxShadow: "0 14px 28px rgba(29,99,200,0.22)",
                    borderColor: "#1d63c8",
                  },
                }}
              />
            </Box>

            {isFetching && !isLoading ? (
              <Typography sx={{ px: 2.5, pb: 2.25, color: "#64748b", fontSize: 13 }}>Updating groups...</Typography>
            ) : null}
          </Paper>
      </Box>

      <Menu
        anchorEl={moreAnchorEl}
        open={Boolean(moreAnchorEl)}
        onClose={closeMoreMenu}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "16px",
            border: "1px solid rgba(226,232,240,0.95)",
            boxShadow: "0 20px 40px rgba(15,23,42,0.10)",
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setAssignGroupId(moreGroupId);
            closeMoreMenu();
          }}
        >
          Assign Device
        </MenuItem>
        <MenuItem
          sx={{ color: "#dc2626" }}
          onClick={() => {
            setDeleteGroupId(moreGroupId);
            closeMoreMenu();
          }}
        >
          Delete Group
        </MenuItem>
      </Menu>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "22px", overflow: "hidden" } }}>
        <DialogTitle sx={{ px: 3, py: 2.25, fontSize: 20, fontWeight: 800, borderBottom: "1px solid rgba(226,232,240,0.95)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {form.id ? "Edit Group" : "Add Group"}
          <IconButton onClick={closeForm}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Box>
              <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: "#334155" }}>Name</Typography>
              <TextField
                fullWidth
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: "#334155" }}>Contact</Typography>
              <TextField
                fullWidth
                value={form.contact}
                onChange={(event) => setForm((prev) => ({ ...prev, contact: event.target.value }))}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={busy}
            sx={{
              flex: 1,
              height: 48,
              borderRadius: "14px",
              fontWeight: 700,
              bgcolor: "#1d63c8",
              "&:hover": { bgcolor: "#114fae" },
            }}
          >
            {busy ? "Saving..." : form.id ? "Update Group" : "Create Group"}
          </Button>
          <Button
            variant="outlined"
            onClick={closeForm}
            sx={{
              flex: 1,
              height: 48,
              borderRadius: "14px",
              fontWeight: 700,
              borderColor: "rgba(203,213,225,0.95)",
              color: "#475569",
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteGroupId)} onClose={() => setDeleteGroupId("")} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "24px" } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Remove Group</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#64748b", lineHeight: 1.7 }}>
            Are you sure you want to remove this group? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.25 }}>
          <Button
            onClick={() => setDeleteGroupId("")}
            variant="outlined"
            sx={{ borderRadius: "12px", minWidth: 110, fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting} sx={{ borderRadius: "12px", minWidth: 110, fontWeight: 700 }}>
            {deleting ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <AddDeviceToGroupModal
        groupId={assignGroupId}
        isOpen={Boolean(assignGroupId)}
        onClose={() => setAssignGroupId("")}
      />
    </Box>
  );
};

export default GroupsList;
